import React, { useMemo, useState, useCallback } from 'react';
import {
  Box, Paper, Stack, Typography, IconButton, Tooltip, Snackbar, Alert, Chip, Button, TextField, MenuItem,
} from '@mui/material';
import { DataGrid, GridToolbarContainer, GridToolbarDensitySelector, GridToolbarExport } from '@mui/x-data-grid';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon, Refresh as RefreshIcon, Search as SearchIcon } from '@mui/icons-material';
import SystemVehicleForm from './SystemVehicleForm';
import { useSystemVehicles } from '../../../hooks/maintenance/useSystemVehicles';
import { useAuthContext } from '../../../context/AuthProvider';

const Toolbar = ({ onAdd, onRefresh, search, setSearch, status, setStatus }) => (
  <GridToolbarContainer>
    <Stack direction="row" alignItems="center" spacing={1} sx={{ p: 1, width: '100%' }}>
      <Button startIcon={<AddIcon />} variant="contained" onClick={onAdd}>Nuevo</Button>

      <TextField
        size="small"
        placeholder="Buscar..."
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        InputProps={{ startAdornment: <SearchIcon fontSize="small" sx={{ mr: 1, opacity: 0.6 }} /> }}
        sx={{ ml: 1, width: 280 }}
      />

      <TextField
        select
        size="small"
        label="Estado"
        value={status}
        onChange={(e) => setStatus(e.target.value)}
        sx={{ width: 140 }}
      >
        <MenuItem value="A">Activos</MenuItem>
        <MenuItem value="I">Inactivos</MenuItem>
        <MenuItem value="">{'Todos (A/I)'}</MenuItem>
      </TextField>

      <Box flex={1} />
      <IconButton onClick={onRefresh}><RefreshIcon /></IconButton>
      <GridToolbarDensitySelector />
      <GridToolbarExport />
    </Stack>
  </GridToolbarContainer>
);

const estChip = (v) =>
  v === 'A' ? <Chip label="Activo" color="success" size="small" /> :
  v === 'I' ? <Chip label="Inactivo" color="default" size="small" /> :
              <Chip label="Eliminado" color="error" size="small" variant="outlined" />;

const SystemVehiclesDashboard = () => {
  const { user } = useAuthContext();
  const {
    items, total, page, pageSize, setPage, setPageSize,
    search, setSearch, status, setStatus,
    isLoading, isError, error,
    createMut, updateMut, deleteMut,
  } = useSystemVehicles();

  const [form, setForm] = useState({ open: false, row: null });
  const [alert, setAlert] = useState({ open: false, message: '', severity: 'success' });

  const showAlert = (message, severity = 'success') => setAlert({ open: true, message, severity });

  const openCreate = () => setForm({ open: true, row: null });
  const openEdit = (row) => setForm({ open: true, row });
  const closeForm = () => setForm({ open: false, row: null });

  const onRefresh = () => {
    // el hook invalida automáticamente al cambiar search/status; aquí podemos forzar:
    // (en este dashboard no guardamos queryClient, pero se puede elevar si quieres)
    setSearch((s) => s); // no-op trigger
  };

  const columns = useMemo(() => ([
    { field: 'sisvehid', headerName: 'ID', width: 90, valueGetter: (p) => p.row.sisvehid ?? p.row.sisvehID },
    { field: 'sisvehnom', headerName: 'Sistema', flex: 1, minWidth: 200 },
    { field: 'sisvehdes', headerName: 'Descripción', flex: 1.5, minWidth: 260 },
    {
      field: 'estreg',
      headerName: 'Estado',
      width: 120,
      renderCell: (params) => estChip(params.value),
      sortable: false,
    },
    {
      field: 'acciones',
      headerName: 'Acciones',
      width: 140,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Stack direction="row" spacing={0.5}>
          <Tooltip title="Editar">
            <IconButton size="small" onClick={() => openEdit(params.row)}>
              <EditIcon color="primary" fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Eliminar">
            <IconButton
              size="small"
              onClick={() => {
                if (!user) return showAlert('Debe iniciar sesión', 'error');
                if (window.confirm(`¿Eliminar "${params.row.sisvehnom}"?`)) {
                  deleteMut.mutate(params.row.sisvehid ?? params.row.sisvehID, {
                    onSuccess: () => showAlert('Sistema eliminado'),
                    onError: (e) => showAlert(e?.response?.data?.error || 'Error al eliminar', 'error'),
                  });
                }
              }}
            >
              <DeleteIcon color="error" fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>
      ),
    },
  ]), [user, deleteMut]);

  const rows = useMemo(
    () =>
      items.map((it) => ({
        ...it,
        id: it.sisvehid ?? it.sisvehID, // DataGrid necesita id
      })),
    [items]
  );

  const handleSubmit = useCallback((data) => {
    if (!user) return showAlert('Debe iniciar sesión', 'error');

    if (form.row) {
      // update
      updateMut.mutate(
        {
          id: form.row.sisvehid ?? form.row.sisvehID,
          data: {
            sisvehnom: data.sisvehnom,
            sisvehdes: data.sisvehdes || null,
            updbyid: user?.EmpID,
            // opcional: estreg: 'A'|'I'
          },
        },
        {
          onSuccess: () => {
            showAlert('Sistema actualizado');
            closeForm();
          },
          onError: (e) => showAlert(e?.response?.data?.error || 'Error al actualizar', 'error'),
        }
      );
    } else {
      // create
      createMut.mutate(
        {
          sisvehnom: data.sisvehnom,
          sisvehdes: data.sisvehdes || null,
          crebyid: user?.EmpID,
        },
        {
          onSuccess: () => {
            showAlert('Sistema creado');
            closeForm();
          },
          onError: (e) => showAlert(e?.response?.data?.error || 'Error al crear', 'error'),
        }
      );
    }
  }, [form.row, user, createMut, updateMut]);

  return (
    <Paper sx={{ p: 2, borderRadius: 2 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
        <Typography variant="h6" fontWeight={700}>Sistemas Vehiculares</Typography>
        <Chip
          label={`${total} resultados`}
          variant="outlined"
          size="small"
        />
      </Stack>

      <Box sx={{ height: 540, width: '100%' }}>
        <DataGrid
          rows={rows}
          columns={columns}
          loading={isLoading}
          disableRowSelectionOnClick
          pagination
          paginationMode="server"
          rowCount={total}
          page={page}
          pageSize={pageSize}
          onPageChange={setPage}
          onPageSizeChange={setPageSize}
          rowsPerPageOptions={[5, 10, 25, 50]}
          components={{ Toolbar }}
          componentsProps={{
            toolbar: {
              onAdd: openCreate,
              onRefresh,
              search, setSearch,
              status, setStatus,
            },
          }}
          sx={{
            '& .MuiDataGrid-columnHeaders': { backgroundColor: '#f3f4f6' },
            borderRadius: 2,
          }}
        />
      </Box>

      <SystemVehicleForm
        open={form.open}
        onClose={closeForm}
        onSubmit={handleSubmit}
        defaultValues={form.row || {}}
      />

      <Snackbar
        open={alert.open}
        autoHideDuration={3500}
        onClose={() => setAlert({ ...alert, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={alert.severity} onClose={() => setAlert({ ...alert, open: false })}>
          {alert.message}
        </Alert>
      </Snackbar>

      {isError && (
        <Snackbar open anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
          <Alert severity="error">{error?.message || 'Error al cargar'}</Alert>
        </Snackbar>
      )}
    </Paper>
  );
};

export default SystemVehiclesDashboard;
